import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { Alert, Button, CircularProgress, InputLabel, MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";
import { forwardRef, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { assetService } from "../../../Core/Api/services";
import { DbMarkerCategory } from "../../../Core/Api/Types/db-marker-category";
import { Languages } from "../../../Core/Api/Types/languages";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

export type ModalExportAssetPdfElementProps = {
  assetId: number;
};

export type ModalExportAssetPdfProps = CommonModalProps<ModalExportAssetPdfElementProps>;

const ModalExportAssetPdf = forwardRef<any, ModalExportAssetPdfProps>((props, ref) => {
  const { t } = useTranslation();
  const { assetId, closeModal } = props;
  const [selectedLanguage, setSelectedLanguage] = useState<Languages>(Languages.EN);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);

  const enqueueSnackbar = useCustomSnackbar();

  const handleDownload = () => {
    if (!isProcessing) {
      setIsProcessing(true);
      assetService
        .ExportAssetAsPdf(
          assetId,
          selectedLanguage,
          selectedCategory !== "" ? (selectedCategory as DbMarkerCategory) : undefined
        )
        .then((success) => {
          setIsProcessing(false);
          if (!success) enqueueSnackbar(t("alert:error.pdfGeneration"), "error");
          else closeModal();
        });
    }
  };

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={t("modal:title.assetPdfExport")}
      reference={ref}
      closeModal={handleCancelClick}
      width={500}
      footer={
        <Stack spacing={1}>
          {isProcessing && <Alert severity="info">{t("alert:info.yourFileIsGenerating")}</Alert>}
          <Stack direction="row" justifyContent="space-between">
            <Button variant="outlined" onClick={handleCancelClick}>
              {t("common:close")}
            </Button>
            <Button variant="outlined" color="success" onClick={handleDownload} disabled={isProcessing}>
              {isProcessing ? <CircularProgress size={20} /> : t("button:exportPdf")}
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Stack spacing={1}>
          <InputLabel htmlFor="select-language">{t("field:label.selectLanguage")}</InputLabel>
          <Select
            id="select-language"
            startAdornment={<LanguageOutlinedIcon />}
            IconComponent={ExpandMoreRoundedIcon}
            sx={{ mb: 1 }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={selectedLanguage}
            onChange={(event: SelectChangeEvent<Languages>) => setSelectedLanguage(event.target.value as Languages)}
            disabled={isProcessing}
          >
            <MenuItem value={Languages.EN}>EN</MenuItem>
            <MenuItem value={Languages.FR}>FR</MenuItem>
          </Select>
        </Stack>
        <Stack spacing={1}>
          <InputLabel htmlFor="select-language">{t("field:label.selectCategory")}</InputLabel>
          <Select
            id="select-language"
            displayEmpty
            startAdornment={<CategoryOutlinedIcon />}
            IconComponent={ExpandMoreRoundedIcon}
            sx={{ mb: 1 }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={selectedCategory}
            onChange={(event: SelectChangeEvent<string>) => setSelectedCategory(event.target.value)}
            disabled={isProcessing}
          >
            <MenuItem value="">{t("menu:item.allCategories")}</MenuItem>
            <MenuItem value={DbMarkerCategory.BatonAutoQc}>{t("menu:item.batonAutoQC")}</MenuItem>
            <MenuItem value={DbMarkerCategory.HybrikAutoQc}>{t("menu:item.hybrikAutoQC")}</MenuItem>
            <MenuItem value={DbMarkerCategory.Human}>{t("common:human")}</MenuItem>
          </Select>
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalExportAssetPdf;
